import { AsyncPipe } from '@angular/common'
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  Output,
} from '@angular/core'

import { TranslatePipe } from '@ngx-translate/core'
import { pick } from 'lodash-es'
import { Dialog } from 'primeng/dialog'

import { ApiErrors, CountryCodes, UserProfile, UserRole } from '@ti/contracts'
import { DeviceService, ErrorHandler } from '@ti/core/common/services'
import { getApiErrorKey, getMessageFromException } from '@ti/core/common/utils'
import { LanguageService } from '@ti/core/ui-kit/i18n'

import { UserFormMode, UserFormModel } from '../../models/user-form.model'
import { BaseUserManager } from '../../services'
import { UserFormComponent } from '../user-form/user-form.component'

@Component({
  selector: 'app-add-user-dialog',
  templateUrl: 'user-add.component.html',
  styleUrl: 'user-add.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [UserFormModel],
  imports: [AsyncPipe, Dialog, TranslatePipe, UserFormComponent],
})
export class UserAddDialogComponent {
  @Output() closed = new EventEmitter<void>()
  @Output() created = new EventEmitter<void>()

  @Input() opened = false
  @Input() user: Partial<UserProfile> = {}
  @Input() mode: UserFormMode = UserFormMode.Fleet
  @Input() country: CountryCodes = CountryCodes.UnitedStates
  @Input() usernamePrefix?: string
  @Input() forceRole?: UserRole

  protected readonly cd = inject(ChangeDetectorRef)
  protected readonly deviceService = inject(DeviceService)
  protected readonly form = inject(UserFormModel)
  protected readonly userManager = inject(BaseUserManager)
  protected readonly errorHandler = inject(ErrorHandler)
  protected readonly languageService = inject(LanguageService)

  public open() {
    this.opened = true
    this.cd.markForCheck()
  }

  public close() {
    this.opened = false
    this.cd.markForCheck()
    this.closed.emit()
    this.form.reset()
  }

  protected async createUser(user: Partial<UserProfile> & { password?: string }) {
    try {
      this.form.setIsLoading(true)
      await this.userManager.create({
        ...user,
        ...pick(this.user, ['tspId', 'fleetId']),
      })
      this.created.emit()
      this.close()

      if (user.username && user.password) {
        await this.form.openCopyCredentialsDialog(pick(user, ['username', 'password']))
      }
    } catch (e: unknown) {
      console.warn(`Cannot create user`, e)

      const errorMsg = getMessageFromException(e)
      if (errorMsg === ApiErrors.authUserWithSameEmailExists) {
        this.form.setEmailError(await this.languageService.translate(getApiErrorKey(errorMsg)))
      } else if (errorMsg === ApiErrors.authUserWithSameUsernameExists) {
        this.form.setUsernameError(await this.languageService.translate(getApiErrorKey(errorMsg)))
      } else {
        await this.errorHandler.notifyUser(e, 'Cannot create user')
      }
    } finally {
      this.form.setIsLoading(false)
    }
  }
}
